import { useCallback } from 'react'
import { useAnalytics } from '@praxis/analytics/client'
import { useAuth } from '@praxis/component-auth'

export const useFirefly = () => {
  const { sendClickEvent, sendViewEvent } = useAnalytics()
  const auth = useAuth()

  const createEvent = useCallback(
    (key, value) => {
      const strVal = String(value)
      const lanId = auth?.session?.userInfo?.lanId

      return {
        event: {
          id: key,
          value: strVal,
        },
        client: {
          employee_login_id: lanId,
        },
      }
    },
    [auth?.session?.userInfo?.lanId],
  )

  /*
  Below are the main event handlers that can be used across the app to send events to Firefly.
  1. handleTrackCustomEvent: This function is created to preserve the original functionality..
  2. handleTrackClickEvent: Specifically for tracking click events.
  3. handleTrackViewEvent: Specifically for tracking view events.
  */

  const handleTrackClickEvent = useCallback(
    (key, value) => {
      sendClickEvent(key, createEvent(key, value))
    },
    [sendClickEvent, createEvent],
  )

  const handleTrackViewEvent = useCallback(
    (key, value) => {
      sendViewEvent(key, createEvent(key, value))
    },
    [sendViewEvent, createEvent],
  )

  const handleTrackCustomEvent = useCallback(
    (type, key, value) => {
      handleTrackClickEvent(key, value)
    },
    [handleTrackClickEvent],
  )
  const handleTrackEvent = useCallback(
    (key, event) => {
      let value
      if (event) {
        value = event
        if (typeof event === 'object') {
          const { target, currentTarget } = event
          value = target.text
            ? target.text
            : target.textContent
              ? target.textContent
              : currentTarget.textContent
                ? currentTarget.textContent
                : currentTarget.getAttribute('aria-label')
                  ? currentTarget.getAttribute('aria-label')
                  : ''
        }
      }
      console.log(event, key, value)
      handleTrackClickEvent(key, value)
    },
    [handleTrackClickEvent],
  )
  // Optional helper: attach a global click listener that automatically
  // sends click events for elements annotated with data-track-click.
  const attachAutomaticClickTracking = useCallback(() => {
    if (typeof document === 'undefined') return () => {}

    const handleDocumentClick = (event) => {
      let el = event.target

      // Walk up the DOM until we find an element with data-track-click
      while (el && el !== document) {
        const eventKey = el.getAttribute?.('data-track-click')
        if (eventKey) {
          const value =
            el.getAttribute?.('data-track-value') ??
            el.value ??
            el.textContent ??
            ''

          handleTrackClickEvent(eventKey, value)
          break
        }
        el = el.parentElement
      }
    }

    document.addEventListener('click', handleDocumentClick, true)

    // Return cleanup function so callers can detach if needed
    return () => {
      document.removeEventListener('click', handleDocumentClick, true)
    }
  }, [handleTrackClickEvent])

  return {
    handleTrackCustomEvent,
    handleTrackClickEvent,
    handleTrackViewEvent,
    handleTrackEvent,
    attachAutomaticClickTracking,
  }
}
